<template>
  <div class="modal-overlay">
    <div class="modal-container">

      <!-- Header -->
      <div class="modal-header">
        <ion-icon name="notifications-outline" class="modal-icon" />
        <div class="modal-title">
          Notificaciones de <span class="username">{{ user.username }}</span>
        </div>
        <div class="header-divider"></div>
      </div>

      <!-- Cuerpo -->
      <div class="modal-body">
        <div v-if="loadingLines" class="loading-spinner">
          <span class="spinner"></span> Cargando líneas...
        </div>

        <div v-else>
          <div class="lines-header">Selecciona las líneas de notificación:</div>

          <div class="lines-list" :class="{ 'disabled': loading }">
            <label
              v-for="line in availableLines"
              :key="line.id"
              class="line-item"
              :class="{ 'not-assigned': !userLines.includes(line.id), 'line-disabled': loading }"
            >
              <input 
                type="checkbox" 
                :checked="userLines.includes(line.id)" 
                @change="toggleLine(line.id)"
                :disabled="loading"
              />
              <span class="custom-checkmark"></span>
              {{ line.line_name }}
            </label>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <template v-if="!loading && !successMessage">
          <button class="btn-close" @click="$emit('close')">
            Cerrar
          </button>
          <button class="btn-save" @click="saveChanges">
            Guardar
          </button>
        </template>

        <template v-else-if="loading">
          <div class="loading-msg">
            <span class="spinner"></span>
            Guardando cambios...
          </div>
        </template>

        <template v-else>
          <div class="success-msg">
            Cambios guardados correctamente
          </div>
        </template>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: "ManageLinesModal",

  // ========== PROPS ==========
  props: {
    user: {
      type: Object,
      required: true
    }
  },

  // ========== ESTADO LOCAL ==========
  data() {
    return {
      availableLines: [],   // Todas las líneas disponibles
      userLines: [],        // IDs de líneas asignadas al usuario
      loading: false,       // Estado de carga
      successMessage: "",    // Estado de éxito tras guardar
      loadingLines: true, // Estado de carga de lineas 
    };
  },

  // ========== CICLO DE VIDA ==========
  async mounted() {
    this.loadingLines = true;
    try {
      await this.fetchAvailableLines();
      await this.fetchUserLines();
    } finally {
      this.loadingLines = false;
    }
  },

  // ========== MÉTODOS ==========
  methods: {
    // Obtener todas las líneas disponibles
    async fetchAvailableLines() {
      try {
        const response = await axios.get('/lines');
        this.availableLines = response.data;
      } catch (error) {
        console.error("Error al obtener líneas:", error);
      }
    },

    // Obtener las líneas actualmente asignadas al usuario
    async fetchUserLines() {
      try {
        const response = await axios.get(`/user-lines/${this.user.id}`);
        this.userLines = response.data.map(line => line.line_id);
      } catch (error) {
        console.error("Error al obtener líneas del usuario:", error);
      }
    },

    // Alternar una línea asignada/desasignada
    toggleLine(lineId) {
      if (this.userLines.includes(lineId)) {
        this.userLines = this.userLines.filter(id => id !== lineId);
      } else {
        this.userLines.push(lineId);
      }
    },

    // Guardar cambios de líneas asignadas
    async saveChanges() {
      this.loading = true;
      this.successMessage = "";

      try {
        // Obtener líneas originales del backend
        const originalUserLines = await axios.get(`/user-lines/${this.user.id}`);
        const originalLines = originalUserLines.data.map(line => line.line_id);

        // Calcular qué líneas se agregan o eliminan
        const linesToAdd = this.userLines.filter(id => !originalLines.includes(id));
        const linesToRemove = originalLines.filter(id => !this.userLines.includes(id));

        // Agregar nuevas líneas
        for (const lineId of linesToAdd) {
          await axios.post(`/user-lines`, {
            user_id: this.user.id,
            line_id: lineId
          });
        }

        // Eliminar líneas removidas
        for (const lineId of linesToRemove) {
          await axios.delete(`/user-lines/${this.user.id}/${lineId}`);
        }

        // Mostrar mensaje de éxito
        this.successMessage = "Líneas actualizadas correctamente";

        // Emitir evento y cerrar después de 1.2s
        setTimeout(() => {
          this.$emit("lines-updated");
          this.$emit("close");
        }, 1200);

      } catch (error) {
        console.error("Error al actualizar líneas:", error);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
/* ========== Animación ========== */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* ========== Overlay ========== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  font-family: 'Manrope', sans-serif;
}

/* ========== Contenedor ========== */
.modal-container {
  background: #ffffff;
  border-radius: 1rem;
  padding: 2rem;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  animation: fadeIn 0.2s ease-in-out;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* ========== Header ========== */
.modal-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
}

.modal-icon {
  font-size: 1.4rem;
  color: #3b82f6;
}

.modal-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
  flex: none;
}

.header-divider {
  flex: 1;
  height: 1.3px;
  background: #e2e8f0;
  max-width: 40%;
  margin-left: auto;
}

.username {
  color: #3b82f6;
  font-weight: 700;
}

/* ========== Lista de líneas ========== */
.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #334155;
  font-weight: 600;
  font-size: 0.95rem;
  padding: 1.2rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  background-color: #f8fafc;
}

.spinner {
  width: 18px;
  height: 18px;
  margin-right: 0.75rem;
  border: 2.5px solid #cbd5e1;
  border-top: 2.5px solid #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.lines-header {
  font-size: 0.92rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.75rem;
  text-align: left;
}

.lines-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.85rem;
}

.line-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.93rem;
  padding: 0.55rem 0.8rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.65rem;
  background-color: #f8fafc;
  color: #1e293b;
  transition: all 0.2s ease;
  cursor: pointer;
  user-select: none;
}

.line-item.not-assigned {
  opacity: 0.55;
}

.line-item:hover {
  border-color: #3b82f6;
  background-color: #eff6ff;
}

.line-item input {
  cursor: pointer;
}

.lines-list.disabled {
  pointer-events: none;
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

.line-item.line-disabled {
  cursor: not-allowed;
  background-color: #f1f5f9;
}

/* ========== Footer ========== */
.modal-footer,
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.btn-save {
  padding: 0.55rem 1.3rem;
  background-color: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 0.65rem;
  font-size: 0.92rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.25s ease;
}

.btn-save:hover {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(37, 99, 235, 0.2);
}

.btn-close {
  padding: 0.55rem 1rem;
  background-color: #f8fafc;
  color: #334155;
  border: 1.5px solid #cbd5e1;
  border-radius: 0.65rem;
  font-size: 0.92rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background-color: #e2e8f0;
  border-color: #94a3b8;
  color: #1e293b;
}

/* ========== Mensaje de éxito / carga ========== */
.loading-msg,
.success-msg {
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
  background-color: #f1f5f9;
  padding: 0.55rem 1.3rem;
  border-radius: 0.65rem;
  border: 1px solid #cbd5e1;
  text-align: center;
  width: 100%;
  justify-content: center;
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.success-msg {
  background-color: #ecfdf5;
  border-color: #34d399;
  color: #059669;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #cbd5e1;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>